import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';

import { ScreenLoader } from '@/components/global/Loader';
import { useTheme } from '@/components/providers/ThemeProvider';
import { supabaseClient } from '@/lib/supabase';

import { SignInForm } from './-components/SignInForm';

export const Route = createFileRoute('/(auth)/sign-in/')({
  component: RouteComponent,
  loader: async () => {
    const {
      data: { session: alreadyLoggedInData },
    } = await supabaseClient.auth.getSession();

    if (alreadyLoggedInData !== null) {
      throw redirect({ to: '/' });
    }
  },
  pendingComponent: ScreenLoader,
});

function RouteComponent() {
  const { resolvedTheme } = useTheme();

  return (
    <section className="min-h-screen bg-gradient-to-br from-primary-50 via-blue-50 to-purple-100 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-400 to-pink-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-gradient-to-br from-blue-300 to-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
        <div className="flex items-center mb-8 text-2xl font-semibold text-gray-900 dark:text-white animate-fade-in">
          <img
            className="mr-2 drop-shadow-lg"
            src={resolvedTheme === 'dark' ? '/LogoWhite.svg' : '/LogoBlack.svg'}
            alt="logo"
            width={140}
            height={140}
          />
        </div>

        <div className="w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 dark:bg-gray-800/80 dark:border-gray-700/50 md:mt-0 sm:max-w-md xl:p-0 animate-slide-up">
          <div className="p-8 space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent md:text-3xl">
                <FormattedMessage id="auth.signIn.title" />
              </h1>
              <div className="mt-2 h-1 w-16 bg-gradient-to-r from-primary-500 to-purple-500 dark:from-blue-400 dark:to-purple-400 rounded-full mx-auto"></div>
            </div>

            <SignInForm />

            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <FormattedMessage id="auth.signIn.noAccount" />
                <Link
                  to="/sign-up"
                  className="ml-1 font-semibold bg-gradient-to-r from-primary-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent hover:from-primary-700 hover:to-purple-700 dark:hover:from-blue-300 dark:hover:to-purple-300 transition-all duration-200"
                >
                  <FormattedMessage id="auth.signIn.signUp" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
